<script setup lang="ts">
import AdminUnpaidRefusal from './AdminUnpaidRefusal.vue';
import type { AdminRefusal } from '../order-management';
import OrderFunding from './OrderFunding.vue';
import OrderModification from './OrderModification.vue';
import { ref, computed, watch } from 'vue';
import {
  cancellationBlocks,
  cancellationLabel,
  type Cancellation,
  type Modification,
  type OrderFunding as Funding,
} from '../order-management';
const props = defineProps<{
  role: 'admin' | 'provider';
  order: {
    deliveryRequestId: string;
    paymentProvider?: string | null;
    funding?: Funding;
    financeReviewRequired?: boolean;
    deliveryCapturePending?: boolean;
    canRefuseUnpaidOrder?: boolean;
    adminRefusal?: AdminRefusal;
    deliveryLifecycleState: string;
    cancellation?: Cancellation;
    modifications?: Modification[];
    assignedBox?: {
      boxId?: string;
      displayName?: string;
      currentDeliveryRequestId?: string | null;
    };
  };
  disabled?: boolean;
}>();
const emit = defineEmits<{ changed: [] }>();
const busy = ref(false),
  error = ref(''),
  success = ref('');
const notDelivered = ref(false),
  notConsumed = ref(false),
  agreement = ref(false);
const reason = ref(''),
  deadline = ref('');
const boxReturned = ref(false);
async function release(): Promise<void> {
  if (busy.value || props.disabled || !boxReturned.value || !props.order.assignedBox?.boxId)
    return;
  busy.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/boxes/${encodeURIComponent(props.order.assignedBox.boxId)}/release`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ deliveryRequestId: props.order.deliveryRequestId }),
      },
    );
    if (!response.ok) throw new Error('Libération refusée. Actualisez la commande.');
    emit('changed');
  } catch (cause) {
    error.value = (cause as Error).message;
  } finally {
    busy.value = false;
  }
}
const controlOpen = computed(
  () => props.role === 'provider' && props.order.cancellation?.status === 'REQUESTED',
);
const decisionOpen = computed(
  () =>
    props.role === 'admin' &&
    props.order.cancellation &&
    [
      'REQUESTED',
      'CHECKED',
      'CONTROL_FAILED',
      'REFUND_PENDING',
      'REFUND_FAILED',
      'SETTLEMENT_PENDING',
    ].includes(props.order.cancellation.status),
);
const requeueOpen = computed(
  () =>
    props.role === 'admin' &&
    ['REFUSEE', 'MODIFICATION_PROPOSEE'].includes(props.order.deliveryLifecycleState) &&
    !cancellationBlocks(props.order),
);
watch(
  () => props.order.deliveryRequestId,
  () => {
    notDelivered.value = false;
    notConsumed.value = false;
    agreement.value = false;
    reason.value = '';
    deadline.value = '';
    error.value = '';
    success.value = '';
  },
);
async function submit(
  action: 'cancellation-check' | 'cancellation-decision' | 'requeue',
  decision?: 'APPROVE' | 'REJECT',
): Promise<void> {
  if (busy.value || props.disabled) return;
  error.value = '';
  success.value = '';
  if (!reason.value.trim()) {
    error.value = 'Ajoutez une note de contrôle ou de décision.';
    return;
  }
  if (
    action === 'requeue' &&
    (!agreement.value || !deadline.value || new Date(deadline.value).getTime() <= Date.now())
  ) {
    error.value = 'Confirmez l’accord client et une date future.';
    return;
  }
  const payload =
    action === 'requeue'
      ? {
          clientAgreement: agreement.value,
          deliveryDeadlineAt: new Date(deadline.value).toISOString(),
          reason: reason.value,
        }
      : action === 'cancellation-check'
        ? {
            cancellationId: props.order.cancellation?.id,
            notDelivered: notDelivered.value,
            notConsumed: notConsumed.value,
            reason: reason.value,
          }
        : { cancellationId: props.order.cancellation?.id, decision, reason: reason.value };
  busy.value = true;
  try {
    const response = await globalThis.fetch(
      `/${props.role}/api/delivery-requests/${encodeURIComponent(props.order.deliveryRequestId)}/${action}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok)
      throw new Error(
        'Décision non enregistrée. Actualisez : un contrôle peut manquer ou le remboursement n’est pas disponible dans cet environnement.',
      );
    success.value = 'Décision enregistrée.';
    emit('changed');
  } catch (cause) {
    error.value = (cause as Error).message;
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <AdminUnpaidRefusal
    v-if="role === 'admin'"
    :key="order.deliveryRequestId"
    :order-id="order.deliveryRequestId"
    :can-refuse="order.canRefuseUnpaidOrder"
    :refusal="order.adminRefusal"
    :disabled="disabled"
    @changed="emit('changed')"
  />
  <p v-if="order.deliveryCapturePending" class="ops-alert" role="alert">
    Encaissement de livraison à vérifier avec l’administrateur. La livraison n’est pas confirmée
    ; ne recommencez pas une nouvelle commande pour cette box.
  </p>
  <OrderFunding
    v-if="order.funding && (!order.adminRefusal || order.adminRefusal.status === 'REFUSED')"
    :funding="order.funding"
  />
  <p v-if="order.financeReviewRequired" class="ops-alert" role="alert">
    Un remboursement a été retourné par la banque. Rapprochement administrateur requis avant
    toute nouvelle opération.
  </p>
  <OrderModification
    v-if="order.modifications?.length"
    :key="order.modifications.at(-1)!.id"
    :role="role"
    :order-id="order.deliveryRequestId"
    :payment-provider="order.paymentProvider"
    :modification="order.modifications.at(-1)!"
    :disabled="disabled"
    @changed="emit('changed')"
  />
  <section
    v-if="order.cancellation || requeueOpen || order.adminRefusal?.status === 'REFUSED'"
    class="order-management"
    aria-label="Gestion de la commande"
  >
    <h3>
      {{
        order.cancellation
          ? 'Annulation client'
          : order.adminRefusal
            ? 'Retour de la Box'
            : 'Reprendre la commande'
      }}
    </h3>
    <p v-if="order.cancellation" class="state">{{ cancellationLabel(order.cancellation) }}</p>
    <p v-if="order.cancellation?.checkedBy">
      Contrôle : {{ order.cancellation.checkedBy }} · {{ order.cancellation.checkedAt }}
    </p>
    <p v-if="order.cancellation?.reason">{{ order.cancellation.reason }}</p>
    <p v-if="order.deliveryLifecycleState === 'CANCELLED'" role="status">
      <template v-if="order.assignedBox?.currentDeliveryRequestId === order.deliveryRequestId">
        La Box affectée reste réservée jusqu’à sa libération explicite par l’administrateur
        après retour au stock.
      </template>
      <template v-else>Cette commande ne réserve plus de Box.</template>
    </p>
    <fieldset :disabled="busy || disabled">
      <template
        v-if="
          role === 'admin' &&
          order.deliveryLifecycleState === 'CANCELLED' &&
          order.assignedBox?.currentDeliveryRequestId === order.deliveryRequestId
        "
      >
        <label
          ><input v-model="boxReturned" type="checkbox" /> La Box est revenue au stock et son
          contenu a été contrôlé</label
        >
        <button type="button" :disabled="!boxReturned" @click="release">
          Libérer la Box pour une nouvelle préparation
        </button>
      </template>
      <template v-if="controlOpen">
        <p>
          Vérifiez physiquement ces deux points. Une case non cochée signifie que le contrôle
          est défavorable.
        </p>
        <label><input v-model="notDelivered" type="checkbox" /> Box non livrée</label>
        <label><input v-model="notConsumed" type="checkbox" /> Contenu non consommé</label>
      </template>
      <template v-if="requeueOpen">
        <p>
          L’offre, le montant prépayé et la commande restent identiques. La commande retournera
          à « À affecter ».
        </p>
        <label
          ><input v-model="agreement" type="checkbox" /> Le client a explicitement accepté cette
          reprise</label
        >
        <label
          >Nouvelle échéance convenue<input v-model="deadline" type="datetime-local"
        /></label>
      </template>
      <label v-if="controlOpen || decisionOpen || requeueOpen"
        >Note de suivi<textarea v-model="reason" maxlength="128" rows="2" />
      </label>
      <button v-if="controlOpen" type="button" @click="submit('cancellation-check')">
        Enregistrer le contrôle
      </button>
      <template v-if="decisionOpen">
        <p>
          {{
            order.funding
              ? 'La partie réservée sera libérée et la partie déjà prélevée sera remboursée après votre décision.'
              : order.paymentProvider === 'stripe'
                ? 'Le remboursement sera confirmé après réponse du prestataire.'
                : 'Cette validation reste une simulation locale.'
          }}
        </p>
        <button
          type="button"
          :disabled="
            !['CHECKED', 'REFUND_PENDING', 'REFUND_FAILED', 'SETTLEMENT_PENDING'].includes(
              order.cancellation?.status ?? '',
            ) || order.financeReviewRequired
          "
          @click="submit('cancellation-decision', 'APPROVE')"
        >
          {{
            order.cancellation?.status === 'SETTLEMENT_PENDING'
              ? 'Vérifier le traitement bancaire'
              : order.funding
                ? 'Valider l’annulation et son règlement'
                : order.cancellation?.status === 'REFUND_PENDING'
                  ? 'Vérifier le remboursement'
                  : order.cancellation?.status === 'REFUND_FAILED'
                    ? 'Réessayer le remboursement'
                    : order.paymentProvider === 'stripe'
                      ? 'Autoriser le remboursement'
                      : 'Valider le remboursement simulé'
          }}
        </button>
        <button
          v-if="
            !['REFUND_PENDING', 'REFUND_FAILED', 'SETTLEMENT_PENDING'].includes(
              order.cancellation?.status ?? '',
            )
          "
          type="button"
          @click="submit('cancellation-decision', 'REJECT')"
        >
          Refuser l’annulation et reprendre le traitement
        </button>
      </template>
      <button v-if="requeueOpen" type="button" @click="submit('requeue')">
        Remettre à affecter
      </button>
    </fieldset>
    <p v-if="error" role="alert">{{ error }}</p>
    <p v-if="success" role="status">{{ success }}</p>
  </section>
</template>

<style scoped>
.order-management {
  margin: 1rem 0;
  padding: 1rem;
  border: 2px solid #eab308;
  border-radius: 1rem;
  background: #fffbeb;
  color: #172033;
  overflow-wrap: anywhere;
}
fieldset {
  border: 0;
  padding: 0;
  min-width: 0;
}
label {
  display: block;
  margin: 0.8rem 0;
}
textarea,
input[type='datetime-local'] {
  display: block;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 0.7rem;
  background: white;
  color: #172033;
  border: 1px solid #64748b;
  border-radius: 0.4rem;
}
button {
  min-height: 44px;
  padding: 0.7rem;
  margin: 0.3rem 0;
  background: #20214f;
  color: white;
  border-radius: 0.6rem;
  width: 100%;
}
button:disabled {
  opacity: 0.45;
}
.state {
  font-weight: 700;
}
</style>
